import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { Link } from 'react-router-dom';
import AdBanner from '../components/AdBanner';
import SEO from '../components/SEO';
import './UnitConverter.css';

function UnitConverter() {
  const [selectedCategory, setSelectedCategory] = useState('length');
  const [fromUnit, setFromUnit] = useState('');
  const [toUnit, setToUnit] = useState('');
  const [fromValue, setFromValue] = useState('');
  const [toValue, setToValue] = useState('');
  const [history, setHistory] = useState([]);

  const categories = useMemo(() => ({
    length: {
      name: 'Length',
      icon: '📏',
      units: {
        mm: { name: 'Millimeter', factor: 0.001 },
        cm: { name: 'Centimeter', factor: 0.01 },
        m: { name: 'Meter', factor: 1 },
        km: { name: 'Kilometer', factor: 1000 },
        in: { name: 'Inch', factor: 0.0254 },
        ft: { name: 'Foot', factor: 0.3048 },
        yd: { name: 'Yard', factor: 0.9144 },
        mi: { name: 'Mile', factor: 1609.34 },
        mil: { name: 'Nautical Mile', factor: 1852 }
      }
    },
    weight: {
      name: 'Weight',
      icon: '⚖️',
      units: {
        mg: { name: 'Milligram', factor: 0.000001 },
        g: { name: 'Gram', factor: 0.001 },
        kg: { name: 'Kilogram', factor: 1 },
        oz: { name: 'Ounce', factor: 0.0283495 },
        lb: { name: 'Pound', factor: 0.453592 },
        ton: { name: 'Metric Ton', factor: 1000 },
        stone: { name: 'Stone', factor: 6.35029 }
      }
    },
    temperature: {
      name: 'Temperature',
      icon: '🌡️',
      units: {
        c: { name: 'Celsius' },
        f: { name: 'Fahrenheit' },
        k: { name: 'Kelvin' },
        r: { name: 'Rankine' }
      }
    },
    volume: {
      name: 'Volume',
      icon: '🥤',
      units: {
        ml: { name: 'Milliliter', factor: 0.001 },
        l: { name: 'Liter', factor: 1 },
        gal: { name: 'Gallon (US)', factor: 3.78541 },
        qt: { name: 'Quart (US)', factor: 0.946353 },
        pt: { name: 'Pint (US)', factor: 0.473176 },
        cup: { name: 'Cup (US)', factor: 0.236588 },
        fl_oz: { name: 'Fluid Ounce (US)', factor: 0.0295735 },
        tbsp: { name: 'Tablespoon', factor: 0.0147868 },
        tsp: { name: 'Teaspoon', factor: 0.00492892 }
      }
    },
    area: {
      name: 'Area',
      icon: '📐',
      units: {
        mm2: { name: 'Square Millimeter', factor: 0.000001 },
        cm2: { name: 'Square Centimeter', factor: 0.0001 },
        m2: { name: 'Square Meter', factor: 1 },
        km2: { name: 'Square Kilometer', factor: 1000000 },
        in2: { name: 'Square Inch', factor: 0.00064516 },
        ft2: { name: 'Square Foot', factor: 0.092903 },
        yd2: { name: 'Square Yard', factor: 0.836127 },
        acre: { name: 'Acre', factor: 4046.86 },
        hectare: { name: 'Hectare', factor: 10000 }
      }
    },
    speed: {
      name: 'Speed',
      icon: '🏃',
      units: {
        ms: { name: 'Meter per Second', factor: 1 },
        kmh: { name: 'Kilometer per Hour', factor: 0.277778 },
        mph: { name: 'Mile per Hour', factor: 0.44704 },
        fps: { name: 'Foot per Second', factor: 0.3048 },
        knot: { name: 'Knot', factor: 0.514444 }
      }
    },
    energy: {
      name: 'Energy',
      icon: '⚡',
      units: {
        j: { name: 'Joule', factor: 1 },
        kj: { name: 'Kilojoule', factor: 1000 },
        cal: { name: 'Calorie', factor: 4.184 },
        kcal: { name: 'Kilocalorie', factor: 4184 },
        wh: { name: 'Watt Hour', factor: 3600 },
        kwh: { name: 'Kilowatt Hour', factor: 3600000 },
        btu: { name: 'BTU', factor: 1055.06 }
      }
    }
  }), []);

  const formatResult = useCallback((value) => {
    if (Math.abs(value) < 0.001 && value !== 0) {
      return value.toExponential(6);
    } else if (Math.abs(value) > 1000000) {
      return value.toExponential(6);
    } else {
      return parseFloat(value.toFixed(10)).toString();
    }
  }, []);

  const convertTemperature = useCallback((value, from, to) => {
    let celsius;
    switch (from) {
      case 'c':
        celsius = value;
        break;
      case 'f':
        celsius = (value - 32) * 5/9;
        break;
      case 'k':
        celsius = value - 273.15;
        break;
      case 'r':
        celsius = (value - 491.67) * 5/9;
        break;
      default:
        celsius = value;
    }
    let result;
    switch (to) {
      case 'c':
        result = celsius;
        break;
      case 'f':
        result = celsius * 9/5 + 32;
        break;
      case 'k':
        result = celsius + 273.15;
        break;
      case 'r':
        result = celsius * 9/5 + 491.67;
        break;
      default:
        result = celsius;
    }
    return formatResult(result);
  }, [formatResult]);

  const convertValue = useCallback((value, from, to, category) => {
    const numValue = parseFloat(value);
    if (isNaN(numValue)) return '';
    if (category === 'temperature') {
      return convertTemperature(numValue, from, to);
    }
    const fromFactor = categories[category].units[from].factor;
    const toFactor = categories[category].units[to].factor;
    return formatResult((numValue * fromFactor) / toFactor);
  }, [categories, convertTemperature, formatResult]);

  // Initialize default units when category changes
  useEffect(() => {
    const units = Object.keys(categories[selectedCategory].units);
    setFromUnit(units[0] || '');
    setToUnit(units[1] || units[0] || '');
    setFromValue('');
    setToValue('');
  }, [selectedCategory, categories]);

  // Convert values
  useEffect(() => {
    if (fromValue && fromUnit && toUnit) {
      const result = convertValue(fromValue, fromUnit, toUnit, selectedCategory);
      setToValue(result);
    } else {
      setToValue('');
    }
  }, [fromValue, fromUnit, toUnit, selectedCategory, convertValue]);

  const handleFromValueChange = (e) => {
    const value = e.target.value;
    setFromValue(value);
    
    // Add to history when conversion is complete
    if (value && toValue && value !== '0') {
      const historyEntry = {
        category: selectedCategory,
        from: `${value} ${categories[selectedCategory].units[fromUnit].name}`,
        to: `${toValue} ${categories[selectedCategory].units[toUnit].name}`,
        timestamp: new Date().toLocaleTimeString()
      };
      setHistory(prev => [historyEntry, ...prev.slice(0, 9)]);
    }
  };

  const swapUnits = () => {
    setFromUnit(toUnit);
    setToUnit(fromUnit);
    setFromValue(toValue);
  };

  const clearAll = () => {
    setFromValue('');
    setToValue('');
  };

  const structuredData = {
    "@context": "https://schema.org",
    "@type": "SoftwareApplication",
    "name": "Unit Converter - Convert Length, Weight, Temperature & More",
    "description": "Free unit converter for length, weight, temperature, volume, area, speed, and more. Convert between metric and imperial units instantly.",
    "url": "https://infi-tools.com/unit-converter",
    "applicationCategory": "UtilitiesApplication",
    "operatingSystem": "Web Browser",
    "offers": {
      "@type": "Offer",
      "price": "0",
      "priceCurrency": "USD"
    },
    "featureList": [
      "Length conversion (meters, feet, inches, kilometers)",
      "Weight conversion (kilograms, pounds, ounces)",
      "Temperature conversion (Celsius, Fahrenheit, Kelvin)",
      "Volume conversion (liters, gallons, cubic meters)",
      "Area conversion (square meters, acres, hectares)",
      "Speed conversion (km/h, mph, m/s)",
      "Real-time conversion",
      "Multiple unit systems"
    ]
  };

  return (
    <div className="unit-converter-page">
      <SEO 
        title="Unit Converter - Convert Length, Weight, Temperature & More"
        description="Free unit converter for length, weight, temperature, volume, area, and speed. Convert between metric and imperial units instantly. Accurate conversion calculator."
        keywords="unit converter, length converter, weight converter, temperature converter, metric conversion, imperial conversion, measurement converter, unit calculator"
        canonicalUrl="https://infi-tools.com/unit-converter"
        structuredData={structuredData}
      />
      <div className="container">
        <div className="converter-header">
          <Link to="/" className="back-link">← Back to Tools</Link>
          <h1>Unit Converter</h1>
        </div>

        {/* Top Ad Banner */}
        <AdBanner adSlot="3457655353" style={{ marginBottom: '20px' }} />

        <div className="converter-container">
          {/* Category Selection */}
          <div className="category-selector">
            <h2>Select Category</h2>
            <div className="categories">
              {Object.entries(categories).map(([key, category]) => (
                <button
                  key={key}
                  className={`category-btn ${selectedCategory === key ? 'active' : ''}`}
                  onClick={() => setSelectedCategory(key)}
                >
                  <span className="category-icon">{category.icon}</span>
                  <span className="category-name">{category.name}</span>
                </button>
              ))}
            </div>
          </div>

          {/* Conversion Interface */}
          <div className="conversion-interface">
            <div className="converter-card">
              <h3>{categories[selectedCategory].name} Converter</h3>
              
              <div className="conversion-row">
                {/* From Section */}
                <div className="conversion-section">
                  <label>From</label>
                  <div className="input-group">
                    <input
                      type="number"
                      value={fromValue}
                      onChange={handleFromValueChange}
                      placeholder="Enter value"
                      className="value-input"
                    />
                    <select
                      value={fromUnit}
                      onChange={(e) => setFromUnit(e.target.value)}
                      className="unit-select"
                    >
                      {Object.entries(categories[selectedCategory].units).map(([key, unit]) => (
                        <option key={key} value={key}>
                          {unit.name}
                        </option>
                      ))}
                    </select>
                  </div>
                </div>

                {/* Swap Button */}
                <div className="swap-section">
                  <button className="swap-btn" onClick={swapUnits} title="Swap units">
                    ⇄
                  </button>
                </div>

                {/* To Section */}
                <div className="conversion-section">
                  <label>To</label>
                  <div className="input-group">
                    <input
                      type="text"
                      value={toValue}
                      readOnly
                      placeholder="Result"
                      className="value-input result"
                    />
                    <select
                      value={toUnit}
                      onChange={(e) => setToUnit(e.target.value)}
                      className="unit-select"
                    >
                      {Object.entries(categories[selectedCategory].units).map(([key, unit]) => (
                        <option key={key} value={key}>
                          {unit.name}
                        </option>
                      ))}
                    </select>
                  </div>
                </div>
              </div>

              <div className="converter-actions">
                <button className="clear-btn" onClick={clearAll}>
                  Clear All
                </button>
                <button 
                  className="copy-btn" 
                  onClick={() => navigator.clipboard?.writeText(toValue)}
                  disabled={!toValue}
                >
                  Copy Result
                </button>
              </div>
            </div>

            {/* Quick Conversions */}
            <div className="quick-conversions">
              <h3>Quick Conversions</h3>
              <div className="quick-conversion-list">
                {fromValue && !isNaN(parseFloat(fromValue)) && (
                  Object.entries(categories[selectedCategory].units)
                    .filter(([key]) => key !== fromUnit)
                    .slice(0, 5)
                    .map(([key, unit]) => {
                      const result = convertValue(fromValue, fromUnit, key, selectedCategory);
                      return (
                        <div key={key} className="quick-conversion-item">
                          <span className="quick-value">{result}</span>
                          <span className="quick-unit">{unit.name}</span>
                        </div>
                      );
                    })
                )}
              </div>
            </div>
          </div>

          {/* History Panel */}
          <div className="history-panel">
            <h3>Recent Conversions</h3>
            {history.length === 0 ? (
              <p className="no-history">No conversions yet</p>
            ) : (
              <div className="history-list">
                {history.map((entry, index) => (
                  <div key={index} className="history-item">
                    <div className="history-conversion">
                      <div className="history-from">{entry.from}</div>
                      <div className="history-arrow">→</div>
                      <div className="history-to">{entry.to}</div>
                    </div>
                    <div className="history-time">{entry.timestamp}</div>
                  </div>
                ))}
              </div>
            )}
            <button 
              className="clear-history"
              onClick={() => setHistory([])}
              disabled={history.length === 0}
            >
              Clear History
            </button>
          </div>
        </div>

        {/* Bottom Ad Banner */}
        <AdBanner adSlot="3613067019" style={{ marginTop: '40px' }} />
      </div>
    </div>
  );
}

export default UnitConverter; 